import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { resolveLanguagePreference, normalizeLanguage } from '../utils/languageUtils';

export const useLanguageSync = () => {
    const { i18n } = useTranslation();
    const { currentUser, isAuthenticated } = useAuth();
    const hasSyncedRef = useRef(false);
    const hasCompletedRef = useRef(false);

    useEffect(() => {
        // Don't run if already completed
        if (hasCompletedRef.current) {
            return;
        }

        // Don't run if already synced for this session
        if (hasSyncedRef.current) {
            return;
        }

        if (isAuthenticated && currentUser) {
            const targetLanguage = resolveLanguagePreference({
                dbLanguage: currentUser.preferred_language,
                i18nLanguage: i18n.language
            });
            const currentLanguage = normalizeLanguage(i18n.language);

            if (currentLanguage !== targetLanguage) {
                i18n.changeLanguage(targetLanguage);
                hasSyncedRef.current = true;

                setTimeout(() => {
                    if (normalizeLanguage(i18n.language) !== targetLanguage) {
                        window.location.reload();
                    }
                }, 1000);
            } else {
                hasSyncedRef.current = true;
            }
        } else if (!isAuthenticated) {
            // Not authenticated, mark as completed
            hasCompletedRef.current = true;
        }
    }, [isAuthenticated, currentUser, i18n]);

    // Mark as completed after a reasonable time
    useEffect(() => {
        const timer = setTimeout(() => {
            hasCompletedRef.current = true;
        }, 5000); // 5 seconds timeout

        return () => clearTimeout(timer);
    }, []);

    return { isSynced: hasSyncedRef.current };
}; 